/**
 * Pre-repair authorization — the manager WORK OVERRIDE dialog.
 *
 * STAGE 0C. The server decides pre-repair authorization at every work entry
 * and refuses with ESTIMATE_REQUIRED or AUTHORIZATION_REQUIRED, recording the
 * front-of-house attention itself. A technician without override authority
 * never sees this dialog — they get a plain "work can't start yet" message.
 * This dialog is for a viewer who holds the override authority (OWNER or
 * MANAGER — see `canOverridePreauth`), including a multi-role owner/manager
 * who is also the technician.
 *
 * An override is a MANAGER DECISION TO LET WORK PROCEED WITHOUT customer
 * authorization. It is not customer consent and is never recorded as one:
 * the server audits it as PREAUTH_OVERRIDE_GRANTED / PREAUTH_OVERRIDE_USED,
 * separately from customer authorizations. It covers ONE work entry within
 * five minutes.
 *
 * "Sign now" is rendered only when the caller supplies a real invoice id that
 * this viewer can open; the timer refusal carries none, so it is not offered
 * from here. A button that cannot succeed is not rendered.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ShieldAlert, FileSignature, ShieldCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

export type PreAuthBlocker = 'ESTIMATE_REQUIRED' | 'AUTHORIZATION_REQUIRED';

interface Props {
  open: boolean;
  onClose: () => void;
  ticketId: string;
  /** Why work is blocked — decided by the server. */
  blocker: PreAuthBlocker;
  /** Whether this viewer holds the override authority (OWNER/MANAGER). */
  canOverride: boolean;
  /** An invoice this viewer can open to capture a signature; omit when unknown. */
  invoiceId?: string | null;
  /** Called after a successful override so the caller can retry the work entry. */
  onOverrideGranted: () => void;
}

export const PreAuthRequiredModal = ({
  open, onClose, ticketId, blocker, canOverride, invoiceId = null, onOverrideGranted,
}: Props) => {
  const navigate = useNavigate();
  const [showOverride, setShowOverride] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSignNow = () => {
    if (!invoiceId) return;
    onClose();
    navigate(`/invoices/${invoiceId}`);
  };

  const handleOverride = async () => {
    if (reason.trim().length < 4) {
      toast({ title: 'Reason required', description: 'Please enter at least 4 characters.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await (supabase as any).rpc('start_ticket_with_preauth_override', {
        _ticket_id: ticketId,
        _reason: reason.trim(),
      });
      if (error) throw error;
      toast({
        title: 'Manager override recorded',
        description: 'Work may start once. This is recorded as a manager override, not customer authorization.',
      });
      onClose();
      onOverrideGranted();
    } catch (e: any) {
      toast({ title: 'Override failed', description: e?.message ?? 'Unknown error', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const title = blocker === 'ESTIMATE_REQUIRED'
    ? 'An estimate is required before work'
    : 'Customer authorization required';
  const description = blocker === 'ESTIMATE_REQUIRED'
    ? 'This shop requires pre-repair authorization, and this ticket has no estimate yet. FOH has been notified.'
    : 'This shop requires the customer to authorize the estimate before work begins, and they have not yet. FOH has been notified.';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-destructive" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {!showOverride ? (
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:justify-between">
            {canOverride && (
              <Button variant="outline" onClick={() => setShowOverride(true)}>
                <ShieldCheck className="h-4 w-4 mr-2" />
                Override and proceed
              </Button>
            )}
            <div className="flex gap-2 sm:ml-auto">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              {invoiceId && (
                <Button onClick={handleSignNow}>
                  <FileSignature className="h-4 w-4 mr-2" />
                  Sign now
                </Button>
              )}
            </div>
          </DialogFooter>
        ) : (
          <div className="space-y-3">
            <div>
              <Label htmlFor="override-reason" className="text-sm">
                Reason for override <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="override-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="e.g. Diagnosis must begin now; I accept responsibility until the customer signs."
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Recorded permanently as a manager work override — not as customer authorization.
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowOverride(false)} disabled={submitting}>
                Back
              </Button>
              <Button onClick={handleOverride} disabled={submitting || reason.trim().length < 4}>
                {submitting ? 'Recording…' : 'Confirm override'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
